/**
 * 거래 ID 생성 + 엔티티 조립 순수 헬퍼
 *
 * @why Repository 구현체(Mock / GoogleSheets)마다 같은 조립 로직이 복사되어 있었고,
 * 특히 ID 생성이 `tx_${Date.now()}` 라서 **같은 밀리초에 여러 건을 만들면 ID가 충돌**했다.
 * 일괄 입력(N행을 한 번에 생성)과 2~3명이 동시에 저장하는 공동 가계부 특성상
 * 실제로 발생 가능한 조건이며, 충돌하면 이후 update/delete가 id로 행을 찾을 때
 * 엉뚱한(먼저 매칭되는) 행을 건드려 조용한 데이터 손상으로 이어진다.
 * 그래서 ID 생성과 엔티티 조립을 이 한 곳으로 모아 모든 구현체가 공유하게 한다.
 */

import type { Transaction, TransactionInput } from '../types/Transaction'

/**
 * 거래 고유 ID 생성
 *
 * @why 시간 기반(Date.now)이 아니라 난수 기반 UUID를 쓰는 이유는 위 파일 주석 참조.
 * `tx_` 접두사는 시트에서 사람이 눈으로 식별하기 위한 기존 관례를 유지한 것이다.
 */
export function generateTransactionId(): string {
  return `tx_${crypto.randomUUID()}`
}

/**
 * 거래 생성 옵션
 */
export interface BuildTransactionOptions {
  /**
   * 기록한 사람(작성자) ID.
   *
   * @why 원래는 항상 현재 로그인 사용자여야 하지만 아직 전역적으로 연동되지 않았다.
   * 미지정 시 기존 동작(spenderId 사용)을 유지해 이번 변경이 단건 등록의
   * 동작을 바꾸지 않도록 하고, 로그인 사용자 연동 시 이 옵션만 채우면 되게 통로를 열어둔다.
   */
  writerId?: string
}

/**
 * 검증을 통과한 입력값을 저장 가능한 Transaction 엔티티로 조립한다.
 *
 * @param input 거래 입력값
 * @param options 작성자 등 부가 정보
 * @returns 새 ID와 생성/수정 시각이 채워진 Transaction
 */
export function buildTransaction(
  input: TransactionInput,
  options: BuildTransactionOptions = {}
): Transaction {
  const now = new Date().toISOString()

  return {
    ...input,
    id: generateTransactionId(),
    minorCategoryId: input.minorCategoryId || null,
    memo: input.memo || '',
    writerId: options.writerId || input.spenderId,
    createdAt: now,
    updatedAt: now,
  }
}
